import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { BadgePercent, ClipboardList, Store, Building2, Factory, Landmark, Info } from "lucide-react";
import { BUSINESS } from "../data/business";

const REGIMES = [
    {
        id: "mei",
        icon: Store,
        label: "MEI",
        limit: "Faturamento até R$ 81 mil/ano",
        taxation: [
            "Valor fixo mensal (DAS), sem alíquota sobre o faturamento",
            "Já inclui INSS, ICMS e/ou ISS",
            "Isento de IRPJ, CSLL, PIS e COFINS",
        ],
        obligations: [
            "Pagar o DAS todo mês",
            "Declaração anual (DASN-SIMEI)",
            "Nota fiscal ao vender para empresas",
            "Controle mensal de receitas",
        ],
    },
    {
        id: "simples",
        icon: Building2,
        label: "Simples Nacional",
        limit: "Faturamento até R$ 4,8 milhões/ano",
        taxation: [
            "Alíquota única sobre o faturamento, a partir de 4%",
            "8 tributos em uma guia só (IRPJ, CSLL, PIS, COFINS, ICMS, ISS, CPP, IPI)",
            "O anexo correto evita pagar imposto a mais",
        ],
        obligations: [
            "Apuração e guia única mensal (PGDAS-D)",
            "Declaração anual (DEFIS)",
            "Folha e pró-labore em dia",
            "Notas fiscais com enquadramento correto",
        ],
    },
    {
        id: "presumido",
        icon: Factory,
        label: "Lucro Presumido",
        limit: "Faturamento até R$ 78 milhões/ano",
        taxation: [
            "IRPJ e CSLL sobre margem de lucro presumida (8% a 32%)",
            "PIS 0,65% e COFINS 3% sobre o faturamento",
            "ISS (serviços) ou ICMS (comércio/indústria)",
        ],
        obligations: [
            "Escrituração contábil e balancetes",
            "Apuração trimestral de IRPJ e CSLL",
            "Obrigações acessórias: EFD, ECF, DCTF",
            "Revisão anual do regime",
        ],
    },
    {
        id: "real",
        icon: Landmark,
        label: "Lucro Real",
        limit: "Obrigatório acima de R$ 78 milhões/ano e para algumas atividades",
        taxation: [
            "IRPJ (15% + adicional) e CSLL (9%) sobre o lucro efetivo",
            "PIS 1,65% e COFINS 7,6%, com direito a créditos",
            "Sem IRPJ/CSLL em período de prejuízo fiscal",
        ],
        obligations: [
            "Escrituração completa e LALUR",
            "ECD, ECF, EFD-Contribuições e DCTF",
            "Balancetes e demonstrações periódicas",
            "Gestão de créditos e compensações",
        ],
    },
];

export const TaxSummary = () => {
    return (
        <section id="tributacao" data-testid="tax-summary-section" className="py-20 lg:py-32 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-2xl reveal-on-scroll">
                    <p className="eyebrow text-xs font-bold uppercase tracking-[0.2em] text-blue-900" data-testid="tax-eyebrow">
                        Entenda os regimes
                    </p>
                    <h1 className="mt-4 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900" data-testid="tax-title">
                        Resumo da tributação e das obrigações de cada regime
                    </h1>
                    <p className="mt-4 text-base text-slate-600 leading-relaxed" data-testid="tax-subtitle">
                        Uma visão clara do que se paga de imposto e do que precisa ser entregue ao fisco em cada
                        enquadramento — para você conversar com o contador já sabendo o básico.
                    </p>
                </div>

                <Tabs defaultValue="mei" className="mt-12" data-testid="tax-tabs">
                    <TabsList className="flex flex-wrap h-auto gap-2 bg-[#F8FAFC] border border-slate-200 rounded-xl p-2">
                        {REGIMES.map((regime) => (
                            <TabsTrigger
                                key={regime.id}
                                value={regime.id}
                                data-testid={`tax-tab-${regime.id}`}
                                className="flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold data-[state=active]:bg-slate-900 data-[state=active]:text-amber-400 transition-colors duration-200"
                            >
                                <regime.icon className="w-4 h-4" />
                                {regime.label}
                            </TabsTrigger>
                        ))}
                    </TabsList>

                    {REGIMES.map((regime) => (
                        <TabsContent key={regime.id} value={regime.id} className="mt-8 animate-fade-up" data-testid={`tax-content-${regime.id}`}>
                            <div className="glass-card rounded-2xl p-8 sm:p-10">
                                <div className="flex flex-wrap items-center gap-3">
                                    <h3 className="text-xl sm:text-2xl font-medium text-slate-900">{regime.label}</h3>
                                    <span className="text-xs font-bold uppercase tracking-[0.12em] text-blue-900 bg-blue-50 border border-blue-200 rounded-full px-3 py-1.5" data-testid={`tax-limit-${regime.id}`}>
                                        {regime.limit}
                                    </span>
                                </div>

                                <div className="mt-8 grid md:grid-cols-2 gap-10">
                                    <div data-testid={`tax-taxation-${regime.id}`}>
                                        <div className="flex items-center gap-2.5 mb-5">
                                            <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-slate-900 text-amber-400">
                                                <BadgePercent className="w-4 h-4" />
                                            </span>
                                            <h4 className="text-base font-bold text-slate-900">Como funciona a tributação</h4>
                                        </div>
                                        <ul className="space-y-3.5">
                                            {regime.taxation.map((item) => (
                                                <li key={item} className="flex items-start gap-3 text-sm text-slate-700 leading-relaxed">
                                                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                                                    {item}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    <div data-testid={`tax-obligations-${regime.id}`}>
                                        <div className="flex items-center gap-2.5 mb-5">
                                            <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-slate-900 text-amber-400">
                                                <ClipboardList className="w-4 h-4" />
                                            </span>
                                            <h4 className="text-base font-bold text-slate-900">Obrigações principais</h4>
                                        </div>
                                        <ul className="space-y-3.5">
                                            {regime.obligations.map((item) => (
                                                <li key={item} className="flex items-start gap-3 text-sm text-slate-700 leading-relaxed">
                                                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                                                    {item}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </TabsContent>
                    ))}
                </Tabs>

                <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4 justify-between bg-blue-50 border border-blue-200 rounded-xl px-6 py-5" data-testid="tax-disclaimer">
                    <p className="flex items-start gap-3 text-sm text-blue-900 leading-relaxed">
                        <Info className="w-4 h-4 mt-0.5 shrink-0 text-blue-900" />
                        Resumo geral e educativo — alíquotas e obrigações variam conforme atividade, município e estado.
                        O enquadramento ideal da sua empresa é definido na análise com o contador.
                    </p>
                    <a
                        href={`${BUSINESS.whatsapp}?text=${encodeURIComponent("Olá! Quero uma análise do regime tributário da minha empresa.")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="tax-cta-link"
                        className="shrink-0 text-sm font-bold text-blue-900 hover:text-blue-950 underline underline-offset-4 transition-colors duration-200"
                    >
                        Pedir análise do meu caso
                    </a>
                </div>
            </div>
        </section>
    );
};
